import type { GQL_ID } from '@@partner:types:graphql'

import React, { useState, useContext, useCallback, useEffect } from 'react'
import { useDebounce } from 'use-debounce'
import { mergeLeft } from 'merge-left-utils'
import {
  useProductSearchEvents,
  ProductEventBus
} from '@@partner:helpers/hooks/useProductSearchEvents'

interface ProductSearchContextValue {
  name: string
  category_id?: GQL_ID
  saledOnly: boolean
  price: {
    gt: number | null
    lt: number | null
  }
}

const FILTER_DEFAULT_STATE: ProductSearchContextValue = {
  name: '',
  category_id: undefined,
  saledOnly: false,
  price: {
    gt: null,
    lt: null
  }
}

interface ProductSearchContext {
  eventBus?: ProductEventBus<any>
  filter: ProductSearchContextValue
  filterQuery: ProductSearchContextValue
  currentShopID?: GQL_ID
  customerID?: GQL_ID
  setShopID(id?: GQL_ID): void
  setCustomerID(id?: GQL_ID): void
  setFilterQuery(newQuery: ProductSearchContextValue): void
  setFilterName(name: string): void
  resetFilterQuery(): void
}

const ProductSearchContext = React.createContext<ProductSearchContext>(
  {} as ProductSearchContext
)

export interface ProductSearchState {
  currentShopID?: GQL_ID
  customerID?: GQL_ID
}
export interface ProductSearchProviderProps {
  shop_id?: GQL_ID
  customer_id?: GQL_ID
}

const ProductSearchProvider: React.FC<ProductSearchProviderProps> = ({
  children,
  shop_id,
  customer_id
}) => {
  const [ filterQuery, setFilterQuery ] =
    useState<ProductSearchContextValue>(FILTER_DEFAULT_STATE)
  const [ state, setState ] = useState<ProductSearchState>({
    currentShopID: shop_id,
    customerID: customer_id
  })

  const eventBus = useProductSearchEvents()

  const [ filter ] = useDebounce(filterQuery, 500)

  const setShopID = useCallback((currentShopID: GQL_ID) => {
    setState((state) => ({ ...state, currentShopID }))
  }, [])
  const setCustomerID = useCallback((customerID: GQL_ID) => {
    setState((state) => ({ ...state, customerID }))
  }, [])

  const resetFilterQuery = useCallback(() => {
    setFilterQuery(FILTER_DEFAULT_STATE)
  }, [])

  useEffect(() => {
    if (shop_id) {
      setShopID(shop_id)
    }
  }, [ setShopID, shop_id ])

  const value: ProductSearchContext = {
    eventBus,
    filter,
    filterQuery,
    setFilterQuery (filters: ProductSearchContextValue) {
      setFilterQuery((q) => mergeLeft(q, filters))
    },
    currentShopID: state.currentShopID,
    setShopID,
    customerID: state.customerID,
    setCustomerID,
    setFilterName (name: string) {
      setFilterQuery({
        ...filterQuery,
        name
      })
    },
    resetFilterQuery
  }

  return (
    <ProductSearchContext.Provider value={value}>
      {children}
    </ProductSearchContext.Provider>
  )
}

export const useProductSearchFilters = () => useContext(ProductSearchContext)

const ProductSearchContextConsumer = ProductSearchContext.Consumer

export {
  ProductSearchContext,
  ProductSearchProvider as ProductSearchContextProvider,
  ProductSearchContextConsumer
}
